import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronRight, ChevronDown, BookOpen, FileText } from "lucide-react";
import { Button } from "./ui/button";

const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [techSupportOpen, setTechSupportOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    // If not on home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  const navItems = [
    { label: 'Home', id: 'hero' },
    { label: 'About', id: 'about' },
    { label: 'Solutions', id: 'coatings' },
    { label: 'Product Finder', id: 'product-finder' },
    { label: 'Applications', id: 'applications' },
    { label: 'Products', id: 'products' },
  ];

  return (
    <header
      className={`fixed top-10 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-lg py-3' : 'bg-white/95 backdrop-blur-md py-4'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => {
            if (location.pathname !== '/') {
              navigate('/');
            } else {
              scrollToSection('hero');
            }
          }}>
            <img
              src="/crimson-logo-with-name.png"
              alt="Crimson Inks"
              className="h-16 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-4 xl:space-x-5">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-gray-700 hover:text-[#8B1538] font-medium transition-colors duration-200 relative group text-sm"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B1538] group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
            
            {/* Knowledge Center Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setTechSupportOpen(true)}
              onMouseLeave={() => setTechSupportOpen(false)}
            >
              <button
                className="flex items-center text-gray-700 hover:text-[#8B1538] font-medium transition-colors duration-200 text-sm"
                data-testid="knowledge-center-menu"
              >
                Knowledge Center
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${techSupportOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Menu */}
              <div className={`absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden transition-all duration-200 ${
                techSupportOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                <Link
                  to="/knowledge-center"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50"
                  data-testid="knowledge-center-articles-link"
                >
                  <div className="w-10 h-10 bg-[#8B1538]/10 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-[#8B1538]" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Articles & Insights</div>
                    <div className="text-xs text-gray-500">UV & LED inks and coatings guides</div>
                  </div>
                </Link>
                <Link
                  to="/gravure-troubleshooting"
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                  data-testid="gravure-troubleshooting-link"
                >
                  <div className="w-10 h-10 bg-[#8B1538]/10 rounded-lg flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-[#8B1538]" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Gravure Troubleshooting Guide</div>
                    <div className="text-xs text-gray-500">Expert ink & press solutions</div>
                  </div>
                </Link>
              </div>
            </div>
            
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-[#8B1538] font-medium transition-colors duration-200 relative group text-sm"
            >
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#8B1538] group-hover:w-full transition-all duration-300"></span>
            </button>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block ml-4">
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-[#8B1538] hover:bg-[#DC143C] text-white px-5 py-2 rounded-md font-semibold text-sm"
            >
              Get Quote
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? 'max-h-[600px] opacity-100 mt-4' : 'max-h-0 opacity-0'
        }`}>
          <div className="pb-4 space-y-3 border-t border-gray-200 pt-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left text-gray-700 hover:text-[#8B1538] font-medium py-2 px-4 rounded-md hover:bg-gray-50 transition-colors"
              >
                {item.label}
              </button>
            ))}
            
            {/* Mobile Knowledge Center */}
            <div className="border-t border-gray-100 pt-3 mt-3">
              <div className="px-4 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Knowledge Center
              </div>
              <Link
                to="/knowledge-center"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:text-[#8B1538] hover:bg-gray-50 rounded-md transition-colors"
                data-testid="mobile-knowledge-center-articles-link"
              >
                <FileText className="h-5 w-5" />
                Articles & Insights
              </Link>
              <Link
                to="/gravure-troubleshooting"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:text-[#8B1538] hover:bg-gray-50 rounded-md transition-colors"
              >
                <BookOpen className="h-5 w-5" />
                Gravure Troubleshooting Guide
              </Link>
            </div>
            
            <button
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left text-gray-700 hover:text-[#8B1538] font-medium py-2 px-4 rounded-md hover:bg-gray-50 transition-colors"
            >
              Contact
            </button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="w-full bg-[#8B1538] hover:bg-[#DC143C] text-white mt-4"
            >
              Get Quote
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
